import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldCheck, CheckCircle2, XCircle, CreditCard } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingFee() {
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get("job") || "";
  const sessionId = searchParams.get("session_id");
  const cancelled = searchParams.get("cancelled") === "1";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [checking, setChecking] = useState(!!sessionId);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    if (sessionId) {
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts += 1;
        try {
          const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
          if (data.status === "collected") {
            clearInterval(poll);
            setPaid(true);
            setChecking(false);
          } else if (attempts > 10) {
            clearInterval(poll);
            setChecking(false);
            setError("Payment is still processing. If your card was charged, the fee is recorded automatically.");
          }
        } catch {
          if (attempts > 10) { clearInterval(poll); setChecking(false); }
        }
      }, 2000);
      return () => clearInterval(poll);
    }
    if (jobId) {
      axios.get(`${API}/jobs/${encodeURIComponent(jobId)}/booking-fee`)
        .then(({ data }) => {
          setInfo(data);
          if (data.paid >= data.fee && data.fee > 0) setPaid(true);
        })
        .catch(() => setError("Booking not found. Check your link or call 03 9113 6005."));
    }
  }, [jobId, sessionId]);

  const pay = async () => {
    setPaying(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/jobs/${encodeURIComponent(jobId)}/booking-fee/card`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || "Could not start card payment. Please call 03 9113 6005.");
      setPaying(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#f4f1ea] text-slate-900">
      <Header />
      <section className="mx-auto max-w-xl px-5 py-16">
        <div className="border border-zinc-200 bg-white p-8 text-center shadow-sm lg:p-12" data-testid="booking-fee-page">
          {checking ? (
            <>
              <Loader2 className="mx-auto mb-6 h-12 w-12 animate-spin text-blaze" strokeWidth={1.4} />
              <h1 className="font-display text-3xl font-semibold uppercase tracking-tighter">Confirming payment…</h1>
              <p className="mt-4 text-sm text-slate-500">Please wait while we confirm your booking fee with the bank.</p>
            </>
          ) : paid ? (
            <>
              <CheckCircle2 className="mx-auto mb-6 h-14 w-14 text-green-600" strokeWidth={1.4} />
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-green-600">Payment Received</p>
              <h1 className="font-display text-3xl font-semibold uppercase tracking-tighter" data-testid="fee-success-heading">Booking Secured</h1>
              <p className="mt-4 text-sm leading-relaxed text-slate-500">
                Your booking fee for job <strong>{jobId}</strong> is paid. A confirmation has been emailed to you — the fee is deducted from your final bill on moving day.
              </p>
            </>
          ) : info ? (
            <>
              <ShieldCheck className="mx-auto mb-6 h-14 w-14 text-blaze" strokeWidth={1.4} />
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-blaze">Secure Your Booking</p>
              <h1 className="font-display text-3xl font-semibold uppercase tracking-tighter">Booking Fee</h1>
              <p className="mt-4 text-sm leading-relaxed text-slate-500">
                Hi {info.customer}, pay the <strong>${info.fee.toFixed(0)} booking fee</strong> to lock in your move on <strong>{info.moving_date}</strong> at <strong>{info.start_time}</strong>. The fee is deducted from your final bill.
              </p>
              <p className="mt-6 font-display text-5xl font-semibold text-slate-900" data-testid="fee-amount">${info.fee.toFixed(0)}</p>
              <button
                onClick={pay}
                disabled={paying}
                className="mt-8 flex w-full items-center justify-center gap-2 bg-blaze px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white disabled:opacity-50"
                data-testid="fee-pay-btn"
              >
                {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                Pay ${info.fee.toFixed(0)} Securely by Card
              </button>
              {cancelled && <p className="mt-4 text-sm font-semibold text-blaze">Payment was cancelled — you can try again anytime.</p>}
              <p className="mt-6 flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest text-slate-400">
                <ShieldCheck className="h-3.5 w-3.5" /> Secure payment via Stripe
              </p>
            </>
          ) : (
            <>
              <XCircle className="mx-auto mb-6 h-14 w-14 text-blaze" strokeWidth={1.4} />
              <h1 className="font-display text-3xl font-semibold uppercase tracking-tighter">Booking Not Found</h1>
              <p className="mt-4 text-sm text-slate-500">{error || "We couldn't find this booking."}</p>
            </>
          )}
          {error && !checking && info && <p className="mt-4 text-sm font-semibold text-blaze">{error}</p>}
          <Link to="/" className="mt-8 inline-block text-xs font-semibold uppercase tracking-widest text-slate-400 hover:text-blaze">Back to moversnearby.com.au</Link>
        </div>
      </section>
      <Footer />
    </main>
  );
}
